import { useCallback, useRef, type TouchEvent } from "react";

export interface UseSwipeGestureOptions {
  /** 左方向 swipe (= startX > endX) 検出時に呼ぶ。 */
  onSwipeLeft?: () => void;
  /** 右方向 swipe (= startX < endX) 検出時に呼ぶ。 */
  onSwipeRight?: () => void;
  /** 上方向 swipe (= startY > endY) 検出時に呼ぶ。 */
  onSwipeUp?: () => void;
  /** 下方向 swipe (= startY < endY) 検出時に呼ぶ。 */
  onSwipeDown?: () => void;
  /** swipe 判定距離 (= px、 default 50)。 これ未満は tap 扱いで callback 不発火。 */
  threshold?: number;
}

export interface UseSwipeGestureHandlers {
  onTouchStart: (event: TouchEvent<HTMLElement>) => void;
  onTouchEnd: (event: TouchEvent<HTMLElement>) => void;
}

/**
 * touch swipe gesture を 4 方向 callback + threshold で受ける hook。
 * touchstart で開始座標を記録、 touchend で終了座標と差分を計算、
 * 水平 / 垂直の優勢方向を判定して該当 callback を発火する。
 *
 * 使用例:
 *   const handlers = useSwipeGesture({
 *     onSwipeRight: () => advanceScene(),
 *     threshold: 60,
 *   });
 *   return <div {...handlers}>scene 本文</div>;
 *
 * tap (= 移動距離 threshold 未満) では callback 不発火、 通常の click handler と
 * 共存可能。 mobile-pwa Phase 4.1 で SceneView 等の advance 経路に配線想定。
 */
export function useSwipeGesture(
  options: UseSwipeGestureOptions,
): UseSwipeGestureHandlers {
  const { onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, threshold = 50 } =
    options;
  const startRef = useRef<{ x: number; y: number } | null>(null);

  const onTouchStart = useCallback((event: TouchEvent<HTMLElement>) => {
    const touch = event.touches[0];
    if (touch === undefined) return;
    startRef.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const onTouchEnd = useCallback(
    (event: TouchEvent<HTMLElement>) => {
      const start = startRef.current;
      if (start === null) return;
      const end = event.changedTouches[0];
      if (end === undefined) {
        startRef.current = null;
        return;
      }
      const dx = end.clientX - start.x;
      const dy = end.clientY - start.y;
      startRef.current = null;

      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      // 移動距離が threshold 未満 → tap 扱いで callback 不発火
      if (absDx < threshold && absDy < threshold) return;

      // 水平 / 垂直の優勢方向で 1 callback のみ発火
      if (absDx > absDy) {
        if (dx > 0) onSwipeRight?.();
        else onSwipeLeft?.();
      } else {
        if (dy > 0) onSwipeDown?.();
        else onSwipeUp?.();
      }
    },
    [onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, threshold],
  );

  return { onTouchStart, onTouchEnd };
}
